import { useSelectableList } from '@fullstory/react-hooks';
import { without } from 'lodash';
import React, { ComponentProps, memo, RefObject, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { LazyLoadList } from '../../components/LazyLoadList';
import { ListDivider } from '../../components/ListDivider';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { NestedDragAndDropMenu } from '../NestedDragAndDropMenu';
import { Select, SelectProps } from '../Select';
import { OptionType, SelectMenuHandle } from '../SelectMenu';
import { LAZY_LOAD_THRESHOLD, useScrollToHandle } from '../SelectMenuOptions';
import { SelectMenuTooltip } from '../SelectMenuTooltip';

export type HeightOptions = 385 | 600;

const MemoizedOption = memo(Select.Option) as typeof Select.Option;

export interface MultiSelectMenuOptionsProps<T> {
  /**
   * The array of options to be displayed in the list of options.
   */
  options: OptionType<T>[];
  /**
   * The callback for when selected menu options change.
   */
  onChange: (option: OptionType<T>[]) => void;
  /**
   * Optional callback for when a menu option is highlighted.
   */
  onHighlight?: (option: OptionType<T> | null) => void;
  /**
   * A prop passed through to the list component that wraps options. If true, the options have a more compact padding.
   */
  compact?: boolean;
  /**
   * If true, prevents option hover state from being cleared on mouse leave.
   */
  alwaysHovered?: boolean;
  /**
   * The selected options.
   */
  selected: OptionType<T>[];
  /**
   * Limit the number of options that can be selected.
   */
  selectedLimit?: number;
  /**
   * The initially selected options, show up at the top of the list.
   */
  initialSelected: OptionType<T>[];
  /**
   * If provided, controls the display of the tooltips provided in the `Option`.
   */
  tooltipProps?: Pick<
    ComponentProps<typeof SelectMenuTooltip>,
    'placement' | 'maxWidth' | 'variant'
  >;
  /**
   * By default (`true`), the selected options are hoisted to the top of the menu. If `hoistSelected`
   * is set to `false` then the selected remain their original place in the menu.
   */
  hoistSelected?: boolean;
  /**
   * Optional ref that enables opening the menu and scrolling to an option or group.
   *
   * Note: ideally we would use `React.forwardRef()` to forward the ref rather than an
   * `innerRef` prop, but `MultiSelectMenu` uses a generic props type which is not
   * supported by `React.forwardRef()`.
   */
  innerRef?: RefObject<SelectMenuHandle>;
  /**
   * If provided, sets max height on the options.
   */
  maxHeight?: HeightOptions;
  /**
   * Allows for sorting of options.
   * Current just supports sorting hoisted options.
   * Must inject Muuri dependency.
   */
  sortable?: {
    behavior: 'hoisted';
    Muuri: React.ComponentProps<typeof NestedDragAndDropMenu>['Muuri'];
  };
  /**
   * If provided, sets the width, used to make muuri play nice.
   */
  menuWidth?: SelectProps['contentWidth'];
}

export function MultiSelectMenuOptions<T>({
  options,
  selected,
  selectedLimit,
  initialSelected,
  onChange,
  onHighlight,
  compact,
  alwaysHovered,
  tooltipProps,
  maxHeight,
  menuWidth,
  hoistSelected = true,
  sortable,
  innerRef,
}: MultiSelectMenuOptionsProps<T>) {
  const intl = useIntl();
  const [overflowContainerRef, setOverflowContainerRef] = useState<HTMLDivElement | null>(null);
  // Disable options over the selected limit
  const isAtSelectedLimit = selectedLimit != null && selected.length >= selectedLimit;
  // Since we can sort unselected items, we can't just use the selected array as the source of truth.
  // We're going to track the order separately by tracking the keys.
  const [sortOrder, setSetOrder] = useState<string[]>(initialSelected.map(i => i.key));
  const sortableHoisted = sortable?.Muuri && sortable.behavior === 'hoisted' && hoistSelected;

  const [hoistedSelectedOptions, nonHoistedOptions] = useMemo(() => {
    if (!hoistSelected) {
      return [[], options];
    }

    return [
      initialSelected
        .filter(option => options.find(maybeOption => maybeOption.key === option.key))
        .sort((a, b) => sortOrder.indexOf(a.key) - sortOrder.indexOf(b.key)),
      options.filter(
        option => !initialSelected.find(maybeOption => maybeOption.key === option.key),
      ),
    ];
  }, [options, initialSelected, hoistSelected, sortOrder]);

  const selectableListItems = useMemo(
    () => [...hoistedSelectedOptions!, ...nonHoistedOptions!],
    [hoistedSelectedOptions, nonHoistedOptions],
  );
  const onClick = useCallback(
    (item: OptionType<T>) => {
      const selectedItem = selected.find(maybeItem => maybeItem.key === item.key);
      onChange(selectedItem ? without(selected, selectedItem) : selected.concat(item));
    },
    [onChange, selected],
  );
  const { getListItemProps } = useSelectableList<HTMLLIElement, OptionType<T>>(
    selectableListItems,
    (maybeItem, item) => maybeItem.key === item.key,
    onClick,
    {
      autoScroll: true,
      alwaysHovered,
      // MultiSelectMenuOptions are only rendered when the portal is open, which means active should always be true
      active: true,
    },
    onHighlight,
  );

  // implement imperative scroll to handlers
  const { groupRef, optionRef } = useScrollToHandle(innerRef);

  const renderOption = (option: OptionType<T>) => {
    const isSelected = !!selected.find(maybeOption => maybeOption.key === option.key);
    const { ref: selectedRef, ...listItemProps } = getListItemProps(option);
    return (
      <MemoizedOption
        selected={isSelected}
        leftIcon={option.icon}
        leftAdornment={option.leftAdornment}
        rightAdornment={
          option.rightAdornment ||
          (option.tooltip && (
            <SelectMenuTooltip tooltipContent={option.tooltip} {...tooltipProps} />
          ))
        }
        subtext={option.description}
        disabled={isAtSelectedLimit && !isSelected ? true : option.disabled}
        compact={compact}
        ref={selectedRef ?? optionRef(option)}
        testId={option.testId}
        {...listItemProps}
      >
        {option.label}
      </MemoizedOption>
    );
  };
  return (
    <Box
      data-component="MultiSelectMenuOptions"
      flexGrow={1}
      overflow="auto"
      maxHeight={maxHeight}
      ref={setOverflowContainerRef}
      {...identifyComponent('multi-select-menu-options')}
    >
      {sortableHoisted && (
        <NestedDragAndDropMenu
          items={hoistedSelectedOptions}
          selected={selected}
          onReorder={keys => {
            setSetOrder(keys);
            const sorted = keys.map(key => selected.find(o => o.key === key)!).filter(o => o);
            const unsorted = selected.filter(o => !sorted.includes(o));
            const newOrder = [...sorted, ...unsorted];
            onChange(newOrder);
          }}
          onChange={onChange}
          width={menuWidth == 'auto' ? undefined : menuWidth}
          Muuri={sortable.Muuri}
        />
      )}
      <LazyLoadList
        numOfItems={options.length}
        active={options.length > LAZY_LOAD_THRESHOLD}
        initialPageSize={LAZY_LOAD_THRESHOLD}
        rootRef={overflowContainerRef}
      >
        {({ shouldRenderIndex }) => (
          <Select.Options>
            {!sortableHoisted &&
              hoistedSelectedOptions.map((option, index) => {
                if (!shouldRenderIndex(index)) return null;

                return (
                  <React.Fragment key={option.key}>
                    {index === 0 && (
                      <Select.OptionGroupHeader
                        compact={compact}
                        actions={[
                          {
                            label: intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' }),
                            onClick: () => onChange([]),
                          },
                        ]}
                      >
                        {intl.formatMessage({ defaultMessage: 'Selected', id: 'byP6IC' })}
                      </Select.OptionGroupHeader>
                    )}
                    {renderOption(option)}
                    {index === hoistedSelectedOptions.length - 1 && <ListDivider />}
                  </React.Fragment>
                );
              })}
            {nonHoistedOptions.map((option, index) => {
              if (!shouldRenderIndex(hoistedSelectedOptions.length + index)) return;
              /**
               * Render the group header if it's a new group, or if the previous option was
               * The selected option (which will always be first). In that case, we want to render the
               * group header again since it's only fixed when it's not the selected.
               */
              const prevOption = nonHoistedOptions[index - 1];
              const renderGroupHeader = option.group && prevOption?.group !== option.group;

              /**
               * We aren't iterating over options within the context of a group, so we need to find them on the fly. Start
               * with 2 pointers, then advance them outwards until the group boundaries are found.
               */
              const getOptionsInGroup = () => {
                const optionsInGroup: Record<string, OptionType<T>> = {};
                let firstIdx = index;
                let lastIdx = index;

                while (nonHoistedOptions[firstIdx - 1]?.group === option.group) firstIdx--;
                while (nonHoistedOptions[lastIdx + 1]?.group === option.group) lastIdx++;

                for (let i = firstIdx; i <= lastIdx; i++) {
                  const o = nonHoistedOptions[i];
                  if (o) {
                    optionsInGroup[o.key] = o;
                  }
                }

                return optionsInGroup;
              };

              return (
                <React.Fragment key={option.key}>
                  {renderGroupHeader &&
                    // If we aren't hoisting the selected options, users can select/clear all from a group for convenience.
                    (hoistSelected ? (
                      <Select.OptionGroupHeader ref={groupRef(option)} compact={compact}>
                        {option.group}
                      </Select.OptionGroupHeader>
                    ) : (
                      <Select.OptionGroupHeader
                        ref={groupRef(option)}
                        compact={compact}
                        hoverable
                        actions={[
                          {
                            label: intl.formatMessage({
                              defaultMessage: 'Clear all',
                              id: 'QW+Q5N',
                            }),
                            onClick: () => {
                              const optionsInGroup = getOptionsInGroup();
                              onChange(selected.filter(s => optionsInGroup[s.key] == null));
                            },
                          },
                          {
                            label: intl.formatMessage({
                              defaultMessage: 'Select all',
                              id: '94Fg25',
                            }),
                            onClick: () => {
                              const optionsInGroup = getOptionsInGroup();
                              onChange(
                                selected
                                  .filter(s => optionsInGroup[s.key] == null)
                                  .concat(Object.values(optionsInGroup)),
                              );
                            },
                          },
                        ]}
                      >
                        {option.group}
                      </Select.OptionGroupHeader>
                    ))}
                  {renderOption(option)}
                </React.Fragment>
              );
            })}
          </Select.Options>
        )}
      </LazyLoadList>
    </Box>
  );
}
